import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Black hero panel matching the login showcase:
 * dark background, red/gold ambient glow, outlined circles,
 * German flag tri-bar and a light, large headline.
 */
export const FlagBar = ({ className }) => (
  <div aria-hidden="true" className={cn('flex h-1.5 w-24 overflow-hidden rounded-full', className)}>
    <span className="flex-1 bg-zinc-600" />
    <span className="flex-1 bg-[#DD0000]" />
    <span className="flex-1 bg-[#FFCE00]" />
  </div>
);

const GermanHero = ({ eyebrow, title, subtitle, actions, children, className }) => (
  <section
    className={cn(
      'relative overflow-hidden rounded-[28px] bg-black p-6 text-white sm:p-10',
      className,
    )}
  >
    {/* Decorative circles & glow (same as login) */}
    <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full border-4 border-zinc-800/70" />
    <div className="pointer-events-none absolute -bottom-48 -right-24 h-80 w-80 rounded-full border-4 border-zinc-800/70" />
    <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[#DD0000]/20 blur-[120px]" />
    <div className="pointer-events-none absolute -bottom-32 left-10 h-72 w-72 rounded-full bg-[#FFCE00]/10 blur-[110px]" />
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
    />

    <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="max-w-2xl space-y-4">
        <FlagBar />
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{eyebrow}</p>
        )}
        <h1 className="text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">{title}</h1>
        {subtitle && <p className="max-w-xl text-sm leading-relaxed text-zinc-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2.5">{actions}</div>}
    </div>

    {children && <div className="relative z-10 mt-8">{children}</div>}
  </section>
);

/** Primary (white on black hero) and secondary (ghost) hero buttons */
export const heroPrimaryBtn =
  'inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-semibold text-black transition hover:bg-zinc-200 active:scale-[0.98]';
export const heroGhostBtn =
  'inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-4 py-2.5 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/[0.12] active:scale-[0.98]';

/** Clean white surface card used under the hero */
export const surfaceCard =
  'rounded-2xl border border-zinc-200 bg-white dark:border-white/10 dark:bg-zinc-900';

export default GermanHero;
